/**
 * buildOptimisticPatientSummary.js
 *
 * Pure utility that constructs a lightweight patient summary stub from
 * client-side form data, suitable for immediate injection into the
 * dashboard's patientsData array after a successful setupNewPatient call.
 *
 * The stub mirrors the shape produced by the Python compute_patient_summary()
 * function (patient_summary_functions.py) closely enough for PatientTable and
 * ArchivedPatientsTable to render it without errors. The real server-computed
 * summary replaces this stub within ~30 seconds via the SWR background refresh.
 *
 * This file has NO React, Firebase, or context dependencies so it can be
 * exhaustively unit-tested as a pure function.
 */

import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';
import { ORG_WORKFLOW_CONFIGS, OBS_OBSERVATION_STATES } from '../constants/workflowConfig';

// Whether a brand-new Autism patient for this org runs Behavioural Observation.
// Reads the org's static workflow config (the same config that will be stamped
// on the patient at creation). Absent obsFlow → enabled, so TOC and every other
// org keep the OBS pill; only orgs that stamp obsFlow.observation = disabled
// (e.g. LMP) omit it, keeping the optimistic dashboard pills in step with the
// server-computed summary that arrives ~30s later.
const isObservationEnabledForOrg = (organisation) =>
  ORG_WORKFLOW_CONFIGS?.[organisation]?.obsFlow?.observation
    !== OBS_OBSERVATION_STATES.DISABLED;

// ─── Stage-to-key mapping ───────────────────────────────────────────────────
// The form sends stages as short labels ('Lead', 'CI', 'SSI', 'DD', 'PAD').
// The summary stores them as 'clinicianLead', 'clinicianCI', etc.
const STAGE_TO_KEY = {
  Lead: 'clinicianLead',
  CI: 'clinicianCI',
  SSI: 'clinicianSSI',
  DD: 'clinicianDD',
  PAD: 'clinicianPAD',
  OBS: 'clinicianOBS',
};

// Custom field keys mirrored from SUMMARY_CUSTOM_FIELD_KEYS in Python.
const SUMMARY_CUSTOM_FIELD_KEYS = ['referenceNumber', 'paymentMethod', 'assessmentType', 'subContractType'];

// ─── Clinician transformation ───────────────────────────────────────────────

/**
 * Convert the form's assignedClinicians map into the summary's clinicians
 * shape and a sorted, deduplicated assignedClinicianIds array.
 *
 * Form shape:   { [uid]: { stages: ['Lead', 'CI'], name: 'Dr Smith' } }
 * Summary shape: { clinicianLead: { clinicianId, clinicianName },
 *                  clinicianDD:   { clinicianId, clinicianName,
 *                                   supportingClinicianIds, supportingClinicianNames },
 *                  ... }
 *
 * DD is special: the first DD clinician becomes primary; any additional
 * DD clinicians become MDT review supporting (same logic as setupNewPatient in main.py).
 */
const transformClinicians = (assignedClinicians) => {
  if (!assignedClinicians || typeof assignedClinicians !== 'object') {
    return { clinicians: null, assignedClinicianIds: [] };
  }

  const cliniciansMap = {};
  const ddClinicians = [];
  const allUids = new Set();

  for (const [uid, data] of Object.entries(assignedClinicians)) {
    if (!uid || !data) continue;
    const stages = data.stages || [];
    const name = data.name || '';
    allUids.add(uid);

    for (const stage of stages) {
      if (stage === 'DD') {
        ddClinicians.push({ clinicianId: uid, clinicianName: name });
      } else {
        const key = STAGE_TO_KEY[stage];
        if (key) {
          cliniciansMap[key] = { clinicianId: uid, clinicianName: name };
        }
      }
    }
  }

  // DD handling: first is primary, rest are MDT review supporting
  if (ddClinicians.length > 0) {
    const primary = ddClinicians[0];
    const supporting = ddClinicians.slice(1);
    cliniciansMap.clinicianDD = {
      clinicianId: primary.clinicianId,
      clinicianName: primary.clinicianName,
      supportingClinicianIds: supporting.map((c) => c.clinicianId),
      supportingClinicianNames: supporting.map((c) => c.clinicianName),
    };
    supporting.forEach((c) => allUids.add(c.clinicianId));
  }

  const hasClinicians = Object.keys(cliniciansMap).length > 0;
  return {
    clinicians: hasClinicians ? cliniciansMap : null,
    assignedClinicianIds: [...allUids].sort(),
  };
};

// ─── Custom fields extraction ───────────────────────────────────────────────

/**
 * Extract only the dashboard-relevant custom fields, matching the Python
 * _extract_custom_fields() filter that only keeps SUMMARY_CUSTOM_FIELD_KEYS.
 */
const extractCustomFields = (processedCustomFields) => {
  if (!processedCustomFields || typeof processedCustomFields !== 'object') {
    return null;
  }
  const result = {};
  let hasAny = false;
  for (const key of SUMMARY_CUSTOM_FIELD_KEYS) {
    const val = processedCustomFields[key];
    if (val !== undefined && val !== null) {
      result[key] = val;
      hasAny = true;
    }
  }
  return hasAny ? result : null;
};

// ─── Progress and locks ─────────────────────────────────────────────────────

/**
 * Compute progressByStage and locks for a brand-new patient (no dataModel).
 *
 * A new patient has:
 *   preQ: 0  — no forms started
 *   ci:   0  — no CI data
 *   ssi: -1  — locked (CI PPD not set), UNLESS the org always expects SSI
 *              (SSI-first, SSI-always-expected, or deferred-DF workflow)
 *   dd:  -1  — locked (reports not approved)
 *   pad:  0  — not locked by default (status !== 'locked')
 *
 * One-clinician SLaM / 3 Borough (appointmentFinishPathModule === 'ssi'):
 *   ssi: -1  — locked because the CI has not been completed yet (the
 *              CI-completion guard in shouldLockAdhdSemiStructuredInterview /
 *              compute_stage_progress precedes the decision check).
 *   dd:  -1  — locked until the SSI is completed
 *   pad: -1  — locked until the SSI is completed
 * (mirrors moduleUnlockUtils and the backend compute_stage_progress).
 *
 * We pass null for patientDataModel because no patient document exists yet;
 * resolveWorkflowConfig falls back to the ORG_WORKFLOW_CONFIGS preset (if one
 * exists for the org) or deriveLegacyConfig. The preset path produces the
 * correct initial progress for orgs whose names do not match the legacy
 * substring checks (e.g. Urban ADHD).
 */
const computeNewPatientProgress = (organisation) => {
  const wf = resolveWorkflowConfig(null, { organisation });
  const isOneClinicianAppointment = wf.appointmentFinishPathModule === 'ssi';
  // SSI-first / SSI-always-expected / deferred-DF orgs start with the SSI
  // unlocked. One-clinician SLaM / 3 Borough start locked because the CI has
  // not been completed yet (the CI-completion guard in
  // shouldLockAdhdSemiStructuredInterview precedes the decision check).
  // Standard orgs (PPD-gated) also start locked.
  const ssiUnlocked =
    !isOneClinicianAppointment &&
    (wf.isSSIFirst || wf.isSSIAlwaysExpected || wf.isDFDeferred);
  const progressByStage = {
    preQ: 0,
    ci: 0,
    ssi: ssiUnlocked ? 0 : -1,
    dd: -1,
    pad: isOneClinicianAppointment ? -1 : 0,
  };
  const locks = {
    ssiLocked: progressByStage.ssi === -1,
    ddLocked: progressByStage.dd === -1,
    padLocked: progressByStage.pad === -1,
  };
  return { progressByStage, locks };
};

/**
 * Compute progressByStage and locks for a brand-new Autism patient.
 *
 * A new Autism patient has:
 *   forms: 0  — no forms started
 *   ddi:   0  — no History/SSI data
 *   obs:   0  — no observation data (omitted entirely when the org disables OBS)
 *   dd:   -1  — locked (reports not approved)
 *   pad:  -1  — locked (DD not completed)
 *
 * @param {string} organisation - Org name, used to decide whether the OBS pill
 *   applies (see isObservationEnabledForOrg).
 */
const computeNewAutismPatientProgress = (organisation) => {
  const progressByStage = {
    forms: 0,
    ddi: 0,
    // Only seed the OBS pill for orgs that actually run Behavioural Observation.
    ...(isObservationEnabledForOrg(organisation) ? { obs: 0 } : {}),
    dd: -1,
    pad: -1,
  };
  const locks = {
    ddLocked: true,
    padLocked: true,
  };
  return { progressByStage, locks };
};

/**
 * Compute progressByStage and locks for a brand-new CYP ADHD patient.
 *
 * CYP is NOT autism: it has no Observation stage ever (so no org check — the
 * autism builder seeded a spurious obs pill for CYP at orgs without an
 * obsFlow.observation=disabled stamp, and nothing downstream removed it), and
 * CI/SSI are separate pills matching the adult ADHD row, not autism's
 * combined ddi. Mirrors cyp_adhd_stage_progress.py's key set exactly, so the
 * optimistic summary and the first server recompute agree.
 */
const computeNewCypAdhdPatientProgress = () => ({
  progressByStage: {
    forms: 0,
    ci: 0,
    ssi: 0,
    dd: -1,
    pad: -1,
  },
  locks: {
    ddLocked: true,
    padLocked: true,
  },
});

// ─── Main builder ───────────────────────────────────────────────────────────

/**
 * Build an optimistic patient summary stub from client-side data.
 *
 * @param {Object} params
 * @param {string}  params.patientId            - Server-generated patient UID (from CF response)
 * @param {Object}  params.formData             - The PatientSetupForm form state
 * @param {Object}  params.assignedClinicians   - Form's clinician map: { [uid]: { stages, name } }
 * @param {Object}  params.processedCustomFields - Processed custom fields from the form
 * @param {Object}  params.userData             - Current clinician's userData from GlobalStateContext
 * @param {string}  params.creatorDisplayName   - auth.currentUser.displayName of the creating user
 * @param {string}  [params.assessmentModule]   - 'adultAdhd' (default) or 'adultAutism'
 *
 * @returns {Object} A summary-shaped object compatible with PatientTable rendering.
 */
export const buildOptimisticPatientSummary = ({
  patientId,
  formData,
  assignedClinicians,
  processedCustomFields,
  userData,
  creatorDisplayName,
  assessmentModule = 'adultAdhd',
}) => {
  const organisation = userData?.organisation || '';
  const { clinicians, assignedClinicianIds } = transformClinicians(assignedClinicians);
  const customFields = extractCustomFields(processedCustomFields);

  // Compute stage-specific progress and lock state for the new patient.
  // Three shapes, not two: legacy ADHD uses the monolithic document model,
  // and the two modular pathways have different pill sets (autism has an
  // org-dependent obs pill and a combined ddi; CYP has neither).
  const { progressByStage, locks } = assessmentModule === 'adultAdhd'
    ? computeNewPatientProgress(organisation)
    : assessmentModule === 'cypAdhd'
      ? computeNewCypAdhdPatientProgress()
      : computeNewAutismPatientProgress(organisation);

  // Workflow shape mirrored from compute_patient_summary so PatientTable can
  // order the stage pills for this row before the server summary arrives.
  // Adult ADHD only; the modular pathways do not carry it (null).
  const workflowModuleOrder = assessmentModule === 'adultAdhd'
    ? (resolveWorkflowConfig(null, { organisation }).isSSIFirst ? 'ssiFirst' : 'ciFirst')
    : null;

  return {
    patientId: patientId || '',
    firstName: formData?.firstName?.trim() || '',
    lastName: formData?.lastName?.trim() || '',
    email: (formData?.email || '').toLowerCase(),
    organisation,
    assessmentModule,
    userType: 'Patient',
    createdAt: Date.now(),
    createdByName: creatorDisplayName || '',
    isArchived: false,
    archiveReason: null,
    archiveNote: null,
    archivedAt: null,
    archivedByName: null,
    customFields,
    clinicians,
    assignedClinicianIds,
    progressByStage,
    locks,
    workflowModuleOrder,
    // A brand-new patient can never be in the one-clinician "ended" state.
    assessmentEndedAfterCi: false,
    assessmentEndedAt: null,
    hasAuthAccount: assessmentModule !== 'cypAdhd',
    hasSetOwnPassword: assessmentModule !== 'cypAdhd' ? false : undefined,
    lastLogin: null,
    informantSetupEmailSent: null,
    allQuestionnairesCompletedEmailSent: null,
    reportsProgress: null,
    reportTimestamps: null,
    formProgress: null,
    informantsSummary: null,
    formFeedbackSummary: null,
    diagnosticDecisionSummary: null,
    _summary: true,
    _optimistic: true,
  };
};

// Export helpers for unit testing
export { transformClinicians, extractCustomFields, computeNewPatientProgress, computeNewAutismPatientProgress, computeNewCypAdhdPatientProgress };
